import React from "react";

/**
 * FilterBar - Barra de filtros reutilizable con selectores dinámicos.
 *
 * Props:
 * - filters: Array<FilterConfig> - Configuración de filtros a renderizar
 *   FilterConfig: {
 *     name: string,          - Nombre/key del filtro
 *     label: string,         - Etiqueta visible
 *     type: 'select' | 'date' | 'range' | 'text',
 *     options?: Array<{ value: any, label: string }>,  - Para type='select'
 *     min?: number,          - Para type='range'
 *     max?: number,          - Para type='range'
 *     placeholder?: string
 *   }
 * - values: object - Valores actuales de los filtros { [name]: value }
 * - onChange: (name: string, value: any) => void - Callback de cambio
 * - onClear: () => void (optional) - Callback para limpiar todos los filtros
 */
export function FilterBar({ filters = [], values = {}, onChange, onClear }) {
  const renderFilter = (filter) => {
    switch (filter.type) {
      case "select":
        return (
          <div key={filter.name} className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">
              {filter.label}
            </label>
            <select
              value={values[filter.name] || ""}
              onChange={(e) => onChange(filter.name, e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple focus:border-transparent"
            >
              <option value="">Todos</option>
              {(filter.options || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );

      case "date":
        return (
          <div key={filter.name} className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">
              {filter.label}
            </label>
            <input
              type="date"
              value={values[filter.name] || ""}
              onChange={(e) => onChange(filter.name, e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple focus:border-transparent"
            />
          </div>
        );

      case "range":
        return (
          <div key={filter.name} className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">
              {filter.label}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={filter.min}
                max={filter.max}
                placeholder="Mín"
                value={values[`${filter.name}Min`] || ""}
                onChange={(e) =>
                  onChange(`${filter.name}Min`, e.target.value)
                }
                className="border border-gray-300 rounded-md px-2 py-2 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-purple focus:border-transparent"
              />
              <span className="text-gray-400 text-sm">–</span>
              <input
                type="number"
                min={filter.min}
                max={filter.max}
                placeholder="Máx"
                value={values[`${filter.name}Max`] || ""}
                onChange={(e) =>
                  onChange(`${filter.name}Max`, e.target.value)
                }
                className="border border-gray-300 rounded-md px-2 py-2 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-purple focus:border-transparent"
              />
            </div>
          </div>
        );

      case "text":
        return (
          <div key={filter.name} className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">
              {filter.label}
            </label>
            <input
              type="text"
              value={values[filter.name] || ""}
              placeholder={filter.placeholder || ""}
              onChange={(e) => onChange(filter.name, e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple focus:border-transparent"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex flex-wrap items-end gap-4">
        {filters.map(renderFilter)}
        {onClear && (
          <div className="flex flex-col justify-end">
            <button
              type="button"
              onClick={onClear}
              className="text-sm text-gray-500 hover:text-gray-700 underline py-2"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FilterBar;
